import { Loader2, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';

import {
  adminClient,
  type AdminAppSharingScope,
  type AdminOrganizationRow,
  type AdminOrgAppSharing,
} from '@/api/adminClient';
import { OrganizationPicker, type OrganizationChoice } from '@/components/org/OrganizationPicker';
import { StatusPill } from '@/components/runs/StatusPill';
import { AdminPageHeader, PAGE_CONTAINER_CLASS } from '@/components/shell/AdminPageHeader';
import { AdminSection } from '@/components/shell/AdminSection';
import { Button } from '@/components/ui/button';
import { LoadError } from '@/components/ui/load-state';
import { useOrgScope } from '@/pages/org/OrgLayout';

const SCOPES: Array<{ id: AdminAppSharingScope; label: string; description: string }> = [
  { id: 'none', label: 'Off', description: 'Its apps stay inside this organization.' },
  { id: 'all', label: 'Every Studio organization', description: 'Its app owners can share with any organization set up for Studio, including ones added later.' },
  { id: 'selected', label: 'Only these organizations', description: 'Its app owners can share only with the organizations chosen here.' },
];

export function AppSharingRuleSection({
  orgId,
  sharing,
  organizations,
  onSaved,
}: {
  orgId: string;
  sharing: AdminOrgAppSharing;
  organizations: AdminOrganizationRow[];
  onSaved: (next: AdminOrgAppSharing) => void;
}) {
  const [scope, setScope] = useState<AdminAppSharingScope>(sharing.scope);
  const [chosen, setChosen] = useState<OrganizationChoice[]>(sharing.organizations);
  const [saving, setSaving] = useState(false);

  const changed =
    scope !== sharing.scope ||
    (scope === 'selected' &&
      chosen.map((organization) => organization.organizationId).sort().join() !==
        sharing.organizations.map((organization) => organization.organizationId).sort().join());
  const valid = scope !== 'selected' || chosen.length > 0;

  const save = async () => {
    setSaving(true);
    try {
      const next = await adminClient.putOrgAppSharing(orgId, {
        scope,
        organizationIds: scope === 'selected' ? chosen.map((organization) => organization.organizationId) : [],
      });
      onSaved(next);
      setScope(next.scope);
      setChosen(next.organizations);
      toast.success('App sharing saved');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to save app sharing');
    } finally {
      setSaving(false);
    }
  };

  return (
    <AdminSection
      title="Who its apps can be shared with"
      description="App owners and admins in this organization share an app from Manage → Users & access. The other organization's owners and admins then choose which of their people use it."
      actions={
        <Button type="button" size="sm" onClick={() => void save()} disabled={!changed || !valid || saving}>
          {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" /> : null}
          Save
        </Button>
      }
    >
      <fieldset className="space-y-2" disabled={saving}>
        <legend className="sr-only">Who its apps can be shared with</legend>
        {SCOPES.map((option) => (
          <label key={option.id} className="flex cursor-pointer items-start gap-3 rounded-lg border border-border px-3 py-2.5 hover:bg-muted/40">
            <input
              type="radio"
              name="app-sharing-scope"
              value={option.id}
              checked={scope === option.id}
              onChange={() => setScope(option.id)}
              className="mt-1"
            />
            <span className="min-w-0">
              <span className="block text-sm font-medium text-foreground">{option.label}</span>
              <span className="block text-xs text-muted-foreground">{option.description}</span>
            </span>
          </label>
        ))}
      </fieldset>

      {scope === 'selected' ? (
        <div className="mt-4 space-y-3">
          {chosen.length > 0 ? (
            <ul className="flex flex-wrap gap-1.5" aria-label="Chosen organizations">
              {chosen.map((organization) => (
                <li key={organization.organizationId} className="inline-flex items-center gap-1 rounded-full border border-border bg-muted/60 py-0.5 pl-2.5 pr-1 text-xs text-foreground">
                  {organization.name}
                  <button
                    type="button"
                    onClick={() => setChosen((current) => current.filter((entry) => entry.organizationId !== organization.organizationId))}
                    className="rounded-full p-0.5 text-muted-foreground hover:bg-secondary hover:text-foreground"
                    aria-label={`Remove ${organization.name}`}
                  >
                    <X className="h-3 w-3" aria-hidden="true" />
                  </button>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-xs text-muted-foreground">Choose at least one organization.</p>
          )}
          <OrganizationPicker
            organizations={organizations}
            exclude={[orgId, ...chosen.map((organization) => organization.organizationId)]}
            onPick={(organization) => setChosen((current) => [...current, organization])}
            disabled={saving}
          />
        </div>
      ) : null}
    </AdminSection>
  );
}

export function AppSharesSection({ shares }: { shares: AdminOrgAppSharing['shares'] }) {
  return (
    <AdminSection title="Shared apps" description="The organization's apps shared with other organizations. A share the rule no longer covers lets nobody in until the rule allows it again.">
      {shares.length === 0 ? (
        <p className="text-sm text-muted-foreground">None of its apps are shared with another organization.</p>
      ) : (
        <ul className="divide-y divide-border rounded-lg border border-border">
          {shares.map((share) => (
            <li key={`${share.projectId}:${share.organizationId}`} className="flex items-center justify-between gap-3 px-3 py-2">
              <span className="min-w-0">
                <span className="block truncate text-sm font-medium text-foreground">{share.title}</span>
                <span className="block truncate text-xs text-muted-foreground">with {share.name}</span>
              </span>
              <StatusPill tone={share.allowed ? 'success' : 'neutral'}>{share.allowed ? 'Open' : 'Blocked by rule'}</StatusPill>
            </li>
          ))}
        </ul>
      )}
    </AdminSection>
  );
}

/**
 * Studio Admin → an organization → App sharing: which other organizations its apps may be
 * shared with (off, every Studio organization, or a chosen few), and the shares its apps have.
 */
export function OrgAppSharingPage() {
  const { orgId } = useOrgScope();
  const [sharing, setSharing] = useState<AdminOrgAppSharing | undefined>();
  const [organizations, setOrganizations] = useState<AdminOrganizationRow[]>([]);
  const [error, setError] = useState<string | undefined>();
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setSharing(undefined);
    setError(undefined);
    Promise.all([adminClient.getOrgAppSharing(orgId), adminClient.listOrganizations()])
      .then(([next, list]) => {
        if (cancelled) return;
        setSharing(next);
        setOrganizations(list.items);
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Failed to load app sharing');
      });
    return () => {
      cancelled = true;
    };
  }, [orgId, reloadKey]);

  return (
    <div className={PAGE_CONTAINER_CLASS}>
      <AdminPageHeader
        title="App sharing"
        description="Whether this organization's apps can be shared with other organizations, and with which. Multi-tenant apps reach other customers this way."
      />
      {error ? (
        <LoadError message={error} onRetry={() => setReloadKey((key) => key + 1)} />
      ) : !sharing ? (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
          Loading app sharing
        </div>
      ) : (
        <div className="space-y-6">
          <AppSharingRuleSection key={sharing.updatedAt ?? 'none'} orgId={orgId} sharing={sharing} organizations={organizations} onSaved={setSharing} />
          <AppSharesSection shares={sharing.shares} />
        </div>
      )}
    </div>
  );
}
